/*
 * rowDuration.ts
 * Calculates the duration string of an appointment or time row from
 * its start and end, minus the break of the row. The duration is
 * calculated from the datetimes when both are known and otherwise
 * from the separate time fields.
 */

import { formatDecimalToHoursAndMinutes } from './convertDecimalToHoursAndMinutes';
import { convertTimeStringToDate } from './dateTimeUtils';

// The fields of a row needed to calculate its duration
export interface DurationRowFields {
    start_datetime?: string | null;
    end_datetime?: string | null;
    start_time?: string | null;
    end_time?: string | null;
    break_duration?: number | string | null;
}

// Calculate the hours between the start and end of the row, null when they cannot be determined
export const calculateGrossDurationHours = (row: DurationRowFields): number | null => {
    // Calculate from the datetimes when both exist
    if (row.start_datetime && row.end_datetime) {
        const startDate = new Date(row.start_datetime);
        const endDate = new Date(row.end_datetime);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;
        return (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
    }

    // Otherwise calculate from the time fields
    if (row.start_time && row.end_time) {
        const startTime = convertTimeStringToDate(row.start_time);
        const endTime = convertTimeStringToDate(row.end_time);
        if (!startTime || !endTime || isNaN(startTime.getTime()) || isNaN(endTime.getTime())) return null;
        return (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
    }

    return null;
};

// Format the duration of the row minus its break, zero when a time is missing and null when the times are invalid
export const formatRowDurationString = (row: DurationRowFields): string | null => {
    // Fall back to zero when one of the times is missing
    if (!row.start_time || !row.end_time) return formatDecimalToHoursAndMinutes(0);

    // Keep the current duration when the times cannot be calculated
    const grossHours = calculateGrossDurationHours(row);
    if (grossHours === null) return null;

    // Subtract the break of the row
    const breakHours = row.break_duration ? Number(row.break_duration) : 0;
    return formatDecimalToHoursAndMinutes(Math.max(grossHours - (isNaN(breakHours) ? 0 : breakHours), 0));
};

// Format the break of a row for the break time field, for example 0.5 becomes 0:30
export const formatBreakDurationString = (breakDuration: number | string | null | undefined): string => {
    if (breakDuration === null || breakDuration === undefined || breakDuration === '') return '';
    const hours = Number(breakDuration);
    if (isNaN(hours)) return '';
    return formatDecimalToHoursAndMinutes(hours);
};
